/* eslint-disable max-lines */
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash-es/debounce'
import moment from 'moment-timezone'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { createMachine } from 'xstate'
import classNames from 'classnames'
import { useMachine } from '@xstate/react'
import Image from 'next/image'
import styles from './StickyMobileOrderFooter.module.css'
import OrderSummary from '../OrderSummary/OrderSummary'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import useGetTotalWithDiscount from '@/hooks/useGetTotalWithDiscount'
import { ButtonContext } from '@/app/_providers/ButtonProvider/ButtonProvider'
import ThemeContext from '@/app/_providers/ThemeProvider/ThemeContext'
import { cartExists, getLineItemsTotal, removingAllItems } from '@/utils/cart.utils'
import { updateBodyClass } from '@/utils/dom.utils'
import { CLASSES, ELEMENT_IDS, TEST_IDS } from '@/config/html-attributes.config'
import { actionButtonVariations } from '../Button'
import TXT from '@/messages'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import { Overlay } from '../Modal/Overlay'
import { Portal } from '../Portal/Portal'
import CartIcon from '../CartIcon/CartIcon'
import { safeScroll } from '@/utils/navigation.utils'
import { TIME_FORMATS } from '@/config/dates.config'
import { formatTimeToAMPM } from '@/utils/dates.utils'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import PortalPassBanner from '../PortalPassBanner/PortalPassBanner'

export type Props = {
  handleRestart?: () => void
  displayConfig: {
    isEvent?: boolean
    isEventList?: boolean
    isCheckout?: boolean
    isConfirmation?: boolean
  }
}
// TYPES
export enum STATES {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
}

export enum EVENTS {
  TOGGLE = 'TOGGLE',
}

// Animation for StickyOrderMobileFooter slideout
export enum CartVariants {
  OPEN = 'open',
  CLOSED = 'closed',
}

/**
 * Formats the given date string for compact sticky-footer display.
 *
 * @param {string | null} date - The date string to be formatted, or null if no date is provided.
 * @returns {string | null} The formatted date string according to the sticky footer format, or null if the input is
 * null.
 */
export const formatDate = (date: string | null, timezone: string): string | null => (
  date
    ? moment(date)
      .tz(timezone)
      .format(TIME_FORMATS.stickyFooterDate)
    : null
)

/**
 * Returns the formatted sticky-footer time text for the selected ticket.
 *
 * @param {boolean} isDayPass - Whether the selected ticket is a day pass.
 * @param {string | null} selectedTime - The selected time, or null when no time is selected.
 * @param {string} timeZone - The site's time zone.
 * @returns {string} The day-pass label, the formatted time, or the sticky-footer fallback.
 */
export const getDateTimeDisplayText = (
  isDayPass: boolean,
  selectedTime: string | null,
  timeZone: string,
): string => (
  isDayPass
    ? TXT.orderSummary.dateTimeDayPass
    : (formatTimeToAMPM(selectedTime, timeZone) || `${TXT.time}: -`)
)

export const cartButtonTransition: Transition = {
  type: 'spring',
  damping: 7,
  mass: 0.5,
  stiffness: 50,
}

/**
 * Create variants for framer-motion
 *
 * @param {number} winHeight - this is window.innerHeight
 * It ensures that the height of the opened order summary
 * will be responsive to changes in screen height
 *
 * @returns {Variants} - OPEN & CLOSED variants
 */
export const getCartButtonVariants = (winHeight: number): Variants => {
  const offset = 20
  let page = 192 + offset
  let footer = 90

  if (typeof document !== 'undefined') {
    const header = document.getElementById(ELEMENT_IDS.MAIN_HEADER)?.offsetHeight || 0
    const timer = document.getElementById(PORTAL_SELECTORS.mobileTimer)?.offsetHeight || 0
    footer = document.getElementById(ELEMENT_IDS.STICKY_FOOTER_CONTAINER)?.clientHeight || 0
    page = winHeight - (header + timer + footer + offset)
  }

  return ({
    [CartVariants.OPEN]: {
      height: `${page}px`,
      bottom: `${footer}px`,
    },
    [CartVariants.CLOSED]: {
      height: 0,
      bottom: 0,
    },
  })
}

/**
 * Scroll to the reservation header from sticky mobile footer actions.
 */
export const handleScrollAction = (): void => {
  const element = document.getElementById(ELEMENT_IDS.RESERVATION_HEADER)

  if (element) {
    const bodyRect = document.body.getBoundingClientRect()
    const elemRect = element.getBoundingClientRect()
    const offset = elemRect.top - bodyRect.top

    safeScroll(offset - 90)
  }
}
export const toggleMachine = createMachine(
  {
    initial: STATES.CLOSED,
    id: 'sticky-mobile-footer',
    states: {
      [STATES.CLOSED]: {
        on: {
          [EVENTS.TOGGLE]: [{
            target: STATES.OPEN,
            guard: { type: 'canOpen' },
          },
          {
            target: STATES.CLOSED,
            actions: ['scroll'],
            guard: { type: 'canScroll' },
          }],
        },
        entry: ['unlock'],
        exit: ['resetScroll'],
      },
      [STATES.OPEN]: { on: { [EVENTS.TOGGLE]: STATES.CLOSED }, entry: ['lock'] },
    },
  },
  {
    actions: {
      lock: () => updateBodyClass('scroll-lock'),
      unlock: () => updateBodyClass(null, 'scroll-lock'),
      // Scroll to your reservation
      scroll: () => handleScrollAction(),
      resetScroll: () => {
        const orderSummary = document.getElementById(ELEMENT_IDS.ORDER_SUMMARY_CONTAINER)
        if (orderSummary) {
          orderSummary.scrollTop = 0
        }
      },
    },
  },
)

/**
 * Displays at the bottom of the page when in mobile layouts. Will display various action items and order details -
 * dependent on the current state of the checkout
 */
const StickyMobileOrderFooter: FunctionComponent<Props> = ({
  handleRestart,
  displayConfig: {
    isEvent,
    isEventList,
    isCheckout = false,
    isConfirmation,
  },
}: Props) => {
  const {
    admissionType,
    activeStartTime: { startTime: selectedTime },
    activeDate,
    localLineItems,
    lineItemsToDelete,
    appliedGiftCards,
  } = useOurStore((state) => state)

  const { timezone } = useSeller()
  const { cart } = useCart()

  const isDayPass = admissionType === 'day_pass'

  const { total, totalWithDiscount, discount, totalMinusTaxes } = useGetTotalWithDiscount(
    { cart, giftCardBalance: appliedGiftCards, localLineItems, lineItemsToDelete },
  )

  const [winHeight, setWinHeight] = useState(0)

  // Context
  const { buttonState } = useContext(ButtonContext)
  const { mobile } = useContext(ThemeContext)

  const existingCart = useMemo((): boolean => cartExists(cart), [cart])

  const lineItemsToDeleteQty = useMemo(() => getLineItemsTotal(lineItemsToDelete), [lineItemsToDelete])

  // lineItems qty
  const lineItemQty = useMemo(
    () => getLineItemsTotal(localLineItems),
    [localLineItems],
  )
  // cart lineItems qty
  const cartQty = useMemo(
    () => getLineItemsTotal(cart.lineItems as Transformed.LineItem[]),
    [cart.lineItems],
  )
  // cart and local lineItems qty
  const totalQty = useMemo(
    () => getLineItemsTotal([...localLineItems, ...cart.lineItems as Transformed.LineItem[]]),
    [localLineItems, cart.lineItems],
  )

  const hideFooter = !mobile
    || isConfirmation
    || (!!isEventList && !existingCart)

  // cart and local lineItems qty minus lineItemsToDelete qty
  const localTotalQty = totalQty - lineItemsToDeleteQty

  const [state, send] = useMachine(
    toggleMachine.provide({
      guards: {
        canOpen: () => localTotalQty > 0 && !isCheckout,
        canScroll: () => totalQty > 0,
      },
    }),
  )
  // cart + local lineItems
  const lineItemsLength = useMemo(
    () => cart.lineItems.length + localLineItems.length,
    [cart.lineItems, localLineItems],
  )

  const noLineItems = lineItemsLength === 0
  const open = state.matches('OPEN')

  // if removing all items show totalMinus taxes, otherwise show totalWithDiscount
  const totalToDisplay = removingAllItems(cartQty, lineItemsToDeleteQty, lineItemQty)
    ? totalMinusTaxes
    : totalWithDiscount

  useEffect(() => {
    // close if the order summary is open and there are no lineItems
    if (noLineItems && open) {
      send({ type: 'TOGGLE' })
    }
  }, [cart.lineItems, localLineItems, noLineItems, open, send])

  // if we are showing the sticky footer add a class to the body
  useEffect(() => {
    if (!hideFooter) {
      updateBodyClass(CLASSES.WITH_STICKY_FOOTER)
    } else {
      updateBodyClass(null, CLASSES.WITH_STICKY_FOOTER)
    }
  }, [hideFooter])

  /**
   * Setting winHeight as state will trigger a rerender
   * which will ensure that the opened ordered summary
   * height is always correct when mobile scrollbars changes
   */
  useEffect(() => {
    const callback = (): void => {
      if (mobile) setWinHeight(window.innerHeight)
    }

    if (window) {
      callback()
      window.onresize = debounce(callback, 50)
    }

    return () => {
      window.onresize = null
    }
  }, [mobile])

  const actionButton = useMemo(
    () => {
      const actionButtonClass = classNames(
        styles.FlexItem,
        styles.PurchaseButton,
      )
      return actionButtonVariations(
        buttonState,
        {
          className: actionButtonClass,
          testId: 'sticky-footer-button',
        },
        open ? () => send({ type: 'TOGGLE' }) : undefined,
      )
    },
    [buttonState, open],
  )

  if (hideFooter) return null

  const {
    viewCartButtonLabel,
    hideCartButtonLabel,
  } = TXT.stickyMobileOrderFooter

  const showCartButton = existingCart || isEvent
  const asideClass = classNames(
    styles.FlexItem,
    styles.Aside,
  )

  const leftAsideElement = {
    cartButton: (
      <button
        className={classNames(styles.CartButton)}
        type="button"
        onClick={() => send({ type: 'TOGGLE' })}
        data-testid="sticky-footer-cart-button"
        aria-label={open ? hideCartButtonLabel : viewCartButtonLabel}
      >
        <CartIcon open={open} quantity={localTotalQty} />
      </button>
    ),
    dateTimeDisplay: (
      <div className={styles.TimeDisplay} data-testid="footer-date-time">
        <div>{formatDate(activeDate, timezone) || `${TXT.date}: -`}</div>
        <div>{getDateTimeDisplayText(isDayPass, selectedTime, timezone)}</div>
      </div>
    ),
  }

  return (
    <Portal selector={`#${PORTAL_SELECTORS.mobileOrderFooter}`}>
      <div data-testid="mobile-sticky-footer">
        {!isCheckout && (
          <>
            {open && <Overlay />}
            <AnimatePresence initial={false}>
              <motion.div
                variants={{ ...getCartButtonVariants(winHeight) }}
                transition={cartButtonTransition}
                animate={open ? CartVariants.OPEN : CartVariants.CLOSED}
                className={classNames(styles.OrderSummary)}
                id={ELEMENT_IDS.ORDER_SUMMARY_CONTAINER}
              >
                <div className={styles.Header}>
                  <button
                    className={styles.CloseIcon}
                    type="button"
                    onClick={() => send({ type: 'TOGGLE' })}
                    aria-label={hideCartButtonLabel}
                  >
                    <Image
                      width={20}
                      height={20}
                      src="/static/svg/close.svg"
                      alt="close"
                      data-testid="summary-close-circle-icon"
                    />
                  </button>
                  <h1 className={styles.Title}>{TXT.titles.components.orderSummary}</h1>
                </div>
                <OrderSummary
                  className={styles.Padding}
                  onDeleteAllTickets={handleRestart}
                  forStickyFooterOrReview
                />
              </motion.div>
            </AnimatePresence>
          </>
        )}
        <div
          className={styles.Container}
          data-testid={TEST_IDS.STICKY_FOOTER_CONTAINER}
          id={ELEMENT_IDS.STICKY_FOOTER_CONTAINER}
        >
          {/*
            Portal Pass banner (A/B treatment). Admission flow only — the events flow and
            checkout have no Portal Pass upsell. Absolutely positioned against this fixed
            container so it always rides directly above the footer bar.
          */}
          {!isEvent && !isEventList && !isCheckout && (
            <PortalPassBanner placement="sticky" />
          )}
          <div
            className={asideClass}
          >
            {showCartButton ? leftAsideElement.cartButton : leftAsideElement.dateTimeDisplay}
          </div>
          {actionButton}
          <div className={asideClass}>
            <div className={classNames(styles.PriceTitle)}>
              {TXT.cartSummary.price}
              <div className={classNames(styles.Price)} data-testid="sticky-footer-total">
                {total > 0 || discount > 0
                  ? totalToDisplay
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default StickyMobileOrderFooter
